import {
  Modal,
  Button,
  Group,
  Select,
  Radio,
  Flex,
  Box,
  rem,
  Tooltip,
  Text,
  TextInput,
  Skeleton,
  Loader,
} from "@mantine/core";
import useStyles from "./style.js";
import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg.jsx";
import { calculateDistanceMiles } from "../../services/UserTracking.js";
import { getDeviceLocation } from "../../utils/geolocation.js";
import {
  searchAddress,
  getAddressByPlaceId,
} from "../../services/dashboard.js";

// Build full address string from facility object for Google API
const buildFullAddress = (facility) => {
  if (!facility) return null;
  const city = facility.city || facility.addressLineTwo || facility.address2;
  const parts = [
    facility.address,
    city,
    facility.state,
    facility.zipcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

// Build address string from client's form1 currentAddress fields
const buildClientAddress = (client) => {
  if (!client?.form1) return null;
  const { currentAddress, currentCity, currentState, currentZipCode } =
    client.form1;
  const parts = [
    currentAddress,
    currentCity,
    currentState,
    currentZipCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

function SelectCheckInType({
  opened,
  close,
  open,
  handleModal,
  selectedFacility,
  setSelectedFacility,
  selectedClient,
  setSelectedClient,
  setDisableClockBtn,
  selectedService,
  setSelectedService,
  setEvvLocationData,
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { allPatients } = useSelector((state) => state?.patient);
  const { allFacilities } = useSelector((state) => state?.facility);
  const [selectedType, setSelectedType] = useState();
  const [facilityValue, setFacilityValue] = useState(null);
  const [warningText, setWarningText] = useState(null);
  // const [origins, setOrigins] = useState(null);
  // const [destinations, setDestinations] = useState(null);
  const [miles, setMiles] = useState(null);
  const [milesError, setMilesError] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const isStaff = profile?.user?.type?.toLowerCase() === "staff";
  const [locationPermission, setLocationPermission] = useState("prompt");
  const [showOverrideAddress, setShowOverrideAddress] = useState(false);
  const [overrideAddress, setOverrideAddress] = useState("");
  const [overrideSuggestions, setOverrideSuggestions] = useState([]);
  const [showOverrideSuggestions, setShowOverrideSuggestions] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [addressNotFound, setAddressNotFound] = useState(false);
  const [isClockLoading, setIsClockLoading] = useState(false);

  const overrideDebounceRef = useRef(null);

  // const facilities =
  //   profile?.user?.facilityId?.filter(
  //     (_f) =>
  //       (_f?.checkInType === selectedType ||
  //         _f?.checkInType === "no-clockin") &&
  //       _f?.deleted === false
  //   ) || [];

  let facilities = [];

  if (isStaff) {
    facilities =
      profile?.user?.facilityId?.filter(
        (_f) =>
          _f?.checkInType === selectedType &&
          _f?.checkInType !== "no-clockin" &&
          _f?.deleted === false,
      ) || [];
  } else {
    facilities =
      allFacilities?.facilities?.filter(
        (_f) =>
          _f?.checkInType === selectedType &&
          _f?.checkInType !== "no-clockin" &&
          _f?.deleted === false,
      ) || [];
  }

  // Find the EVV facility for the selected client by matching client's facilityId
  const getClientEvvFacility = () => {
    if (!selectedClient) return null;
    const client = allPatients?.find((_p) => _p?.id === selectedClient);
    const clientFacilityIds = (client?.facilityId || []).map((f) =>
      typeof f === "string" ? f : f?.id || f?._id,
    );
    // Find a facility the client belongs to that has EVV enabled
    return (
      facilities?.find(
        (f) =>
          clientFacilityIds.includes(f?.id || f?._id) &&
          f?.evvRequired === "yes",
      ) || null
    );
  };

  const clientEvvFacility =
    selectedType === "client" ? getClientEvvFacility() : null;

  // Compute EVV max distance for the current selection (facility or client)
  const currentMaxDistance = (() => {
    if (selectedType === "facility" && selectedFacility) {
      return selectedFacility?.evvMaxDistance || 0.25;
    }
    if (selectedType === "client" && clientEvvFacility) {
      return clientEvvFacility.evvMaxDistance || 0.25;
    }
    return 0.25;
  })();

  const clockInHandler = () => {
    handleModal("clock in");
  };

  const onChangeFacility = (value) => {
    setSelectedClient(null);
    setAddressNotFound(false);
    setMiles(null);
    setMilesError(false);
    setFacilityValue(value);
    const facilities = isStaff
      ? profile?.user?.facilityId
      : allFacilities?.facilities;
    let facility_index = facilities?.findIndex((_f) => _f?.id === value);
    // set facility object for payload
    setSelectedFacility(facilities[facility_index]);
    // Call immediately — pass the facility object directly since state hasn't updated yet
    if (userLocation) calculateDistance({ facilityObj: facilities[facility_index] });

    if (!facilities[facility_index]?.checkInType) {
      setWarningText("Ask admin to assign chock in type to the facility");
    } else {
      setWarningText(null);
    }
  };

  // Only fires when geolocation resolves after a selection was already made,
  // or when overrideAddress changes. Direct selections call calculateDistance inline.
  useEffect(() => {
    if (!userLocation) return;
    if (selectedType === "client") {
      if (selectedClient) calculateDistance({ clientId: selectedClient });
    } else {
      if (selectedFacility) calculateDistance({ facilityObj: selectedFacility });
    }
  }, [userLocation, overrideAddress]);

  const onChangeClient = (value) => {
    setSelectedClient(value);
    setAddressNotFound(false);
    setSelectedAddress(null);
    setMiles(null);
    setMilesError(false);
    setShowOverrideAddress(false);
    setOverrideAddress("");
    setOverrideSuggestions([]);
    // Call immediately — even if same client is re-selected, state change won't re-trigger the effect
    if (userLocation) calculateDistance({ clientId: value });
  };

  const onChangeService = (value) => {
    setSelectedService(value);
  };

  const handleOverrideSearch = (value) => {
    setOverrideAddress(value);
    if (overrideDebounceRef.current) clearTimeout(overrideDebounceRef.current);
    if (!value || value.length < 3) {
      setOverrideSuggestions([]);
      setShowOverrideSuggestions(false);
      return;
    }
    overrideDebounceRef.current = setTimeout(async () => {
      setOverrideLoading(true);
      try {
        const results = await searchAddress(value);
        setOverrideSuggestions(results || []);
        setShowOverrideSuggestions(true);
      } catch (e) {
        setOverrideSuggestions([]);
      }
      setOverrideLoading(false);
    }, 400);
  };

  const handleSelectOverrideAddress = async (suggestion) => {
    setShowOverrideSuggestions(false);
    setOverrideLoading(true);
    try {
      const placeDetails = await getAddressByPlaceId(suggestion.place_id);
      const formattedAddr =
        placeDetails?.result?.formatted_address || suggestion.formatted_address;
      setOverrideAddress(formattedAddr);
    } catch (e) {
      setOverrideAddress(suggestion.formatted_address);
    }
    setOverrideLoading(false);
  };

  const radioChangeHandler = (value) => {
    setFacilityValue(null);
    setSelectedType(value);
    // setSelectedType({f_type: null, f_id: null});
    // setSelectedType({f_type: value, f_id: null});
  };

  let types = [];
  if (isStaff) {
    types = profile?.user?.facilityId
      ?.filter((f) => f?.deleted === false)
      ?.map((f) => f?.checkInType);
  } else {
    types = allFacilities?.facilities
      ?.filter((f) => f?.deleted === false)
      ?.map((f) => f?.checkInType);
  }

  const checkInTypes = new Set(types);

  // const checkInTypes = new Set(
  //   profile?.user?.facilityId
  //   ?.filter((f) => f?.deleted === false)
  //   ?.map((f) => f?.checkInType)
  // );

  const hasFacility = checkInTypes.has("facility");
  const hasClient = checkInTypes.has("client");

  const boxWidth = hasFacility && hasClient ? "48%" : "100%";

  useEffect(() => {
    if (profile?.user?.facilityId) {
      setDisableClockBtn(false);
      for (var a = 0; a < profile?.user?.facilityId?.length; a++) {
        let i = profile?.user?.facilityId[a];
        if (i?.checkInType !== "no-clockin") {
          setDisableClockBtn(false);
        }
      }
    }
  }, []);

  const icon = (
    <DropDownArrowSvg
      color={"#94A3B8"}
      style={{ width: rem(1), height: rem(1) }}
    />
  );

  const handleClose = () => {
    setSelectedFacility(null);
    setSelectedClient(null);
    setSelectedType(null);
    setFacilityValue(null);
    setWarningText(null);
    close();
  };

  useEffect(() => {
    const fetchLocation = async () => {
      const loc = await getDeviceLocation();
      if (loc) {
        setUserLocation(loc);
        setLocationPermission("granted");
      } else {
        setLocationPermission("denied");
      }
    };
    fetchLocation();
  }, []);

  // Reset all internal state when the modal opens to prevent stale data
  useEffect(() => {
    if (opened) {
      setSelectedType(null);
      setFacilityValue(null);
      setSelectedFacility(null);
      setSelectedClient(null);
      setWarningText(null);
      setMiles(null);
      setMilesError(false);
      setSelectedAddress(null);
      setAddressNotFound(false);
      setShowOverrideAddress(false);
      setOverrideAddress("");
      setOverrideSuggestions([]);
      setEvvLocationData?.(null);
    }
  }, [opened]);

  useEffect(() => {
    if (selectedType) {
      setMiles(null);
      setSelectedAddress(null);
      setSelectedFacility(null);
      setSelectedClient(null);
      setFacilityValue(null);
    }
  }, [selectedType]);

  const calculateDistance = async ({ clientId = selectedClient, facilityObj = selectedFacility } = {}) => {
    try {
      setIsClockLoading(true);
      const origins = `${userLocation.lat},${userLocation.lng}`;

      let destination;

      if (selectedType === "client") {
        const client = allPatients?.find((_p) => _p?.id === clientId);
        destination = overrideAddress || buildClientAddress(client);

        if (!destination) {
          setIsClockLoading(false);
          setAddressNotFound(true);
          const isAdminRole = profile?.user?.role === "admin";
          if (isAdminRole) {
            const goEdit = await swal({
              title: "Address Not Found",
              text: "This client does not have a current address. Please add an address to continue.",
              icon: "warning",
              buttons: {
                cancel: "Cancel",
                confirm: { text: "Go to Client Edit", value: true },
              },
            });
            if (goEdit) {
              handleClose();
              navigate("/home/patient", {
                state: { clientDetails: client, edit: true, disable: true },
              });
            }
          } else {
            swal({
              title: "Address Not Found",
              text: "This client does not have a current address. Please contact your admin to add the client's address.",
              icon: "warning",
              button: "OK",
            });
          }
          return;
        }
      } else {
        destination = buildFullAddress(facilityObj);

        if (!destination) {
          setIsClockLoading(false);
          setAddressNotFound(true);
          const isAdminRole = profile?.user?.role === "admin";
          if (isAdminRole) {
            const goEdit = await swal({
              title: "Address Not Found",
              text: "This facility does not have an address on file. Please add an address to continue.",
              icon: "warning",
              buttons: {
                cancel: "Cancel",
                confirm: { text: "Go to Facility Edit", value: true },
              },
            });
            if (goEdit) {
              handleClose();
              navigate(`/home/facility/${facilityObj?.id}/edit`);
            }
          } else {
            swal({
              title: "Address Not Found",
              text: "This facility does not have an address on file. Please contact your admin to add the facility's address.",
              icon: "warning",
              button: "OK",
            });
          }
          return;
        }
      }

      let payload = {
        origin: origins,
        destination,
        // units: 'imperial' // or 'imperial'
        mode: "WALKING", // or 'imperial'
      };
      let result = await calculateDistanceMiles(payload);
      if (result?.tracking) {
        let legItem = result?.tracking?.legs?.[0];
        let meeterDistance = legItem?.distance?.value;
        let calculatedMiles =
          Math.round((meeterDistance * 0.000621371192 + Number.EPSILON) * 100) /
          100;

        setMiles(calculatedMiles);
        setSelectedAddress(legItem?.end_address);
        if (calculatedMiles > currentMaxDistance) {
          setMilesError(true);
        } else {
          setMilesError(false);
        }
        // Pass EVV location data up to parent
        setEvvLocationData?.({
          evvLocation: {
            lat: userLocation?.lat,
            lng: userLocation?.lng,
            accuracy: userLocation?.accuracy || null,
          },
          evvDistanceToSite: calculatedMiles,
          evvMatchedAddress: legItem?.end_address || null,
          locationSource: userLocation?.source || null,
          overrideAddress: overrideAddress || null,
        });
      } else {
        setSelectedAddress(destination);
        setMiles(null);
        // Still pass location data even when distance calc fails
        if (userLocation?.lat && userLocation?.lng) {
          setEvvLocationData?.({
            evvLocation: {
              lat: userLocation?.lat,
              lng: userLocation?.lng,
              accuracy: userLocation?.accuracy || null,
            },
            evvDistanceToSite: null,
            evvMatchedAddress: destination || null,
            locationSource: userLocation?.source || null,
            overrideAddress: overrideAddress || null,
          });
        }
      }
    } catch (err) {
      console.error(err);
      setIsClockLoading(false); // 🔥 stop loading
    } finally {
      setIsClockLoading(false); // 🔥 stop loading
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        size="lg"
        title="Clock In"
        centered
        radius={16}
        zIndex={1000}
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        className={classes.modal}
      >
        {/* <div className={classes.clockIconParent}>
          <img src={trackerIcon} alt="Tracker Icon" />
        </div>
        <p className={classes.warn}>Select Check In Type</p> */}
        {/* 
        <Select
          className={classes.facilityModal}
          label="Select Facility"
          size="md"
          data={profile?.user?.facilityId?.map((_f) => ({
            label: _f?.name,
            value: _f?.id,
          }))}
          placeholder="Select Facility"
          value={facilityValue}
          onChange={onChangeFacility}
          //disabled={view}
        /> */}

        <Flex align={"center"} justify="center" gap="md">
          <Box
            className={classes.noticeBox}
            style={{
              background: `${milesError ? "#FECDCA" : "#a6f4c580"}`,
              color: `${milesError ? "#B42318" : "#027A48"}`,
            }}
          >
            <Box
              className={classes.notice}
              style={{ color: `${milesError ? "#B42318" : "#027A48"}` }}
            >
              Notice
            </Box>
            {milesError ? (
              <Box className={classes.noticeText}>
                You can't Clock in until you are within{" "}
                <b>{currentMaxDistance}</b> Miles of the address
              </Box>
            ) : (
              <Box className={classes.noticeText}>You are set to Clock In</Box>
            )}
          </Box>
        </Flex>
        {checkInTypes.has("facility") || checkInTypes.has("client") ? (
          <Radio.Group
            name="check_in_type"
            label="Clock-in Type"
            required
            value={selectedType}
            onChange={radioChangeHandler}
            mt={10}
          >
            <Group mt="xs" className={classes.radioGroup}>
              {checkInTypes.has("facility") && (
                <Box
                  className={classes.radioBox}
                  style={{
                    cursor: "pointer",
                    border:
                      selectedType === "facility" ? "1px solid #FEB273" : "",
                    backgroundColor:
                      selectedType === "facility" ? "#feb2732b" : "",
                    width: boxWidth,
                  }}
                  onClick={() => setSelectedType("facility")}
                >
                  <Radio
                    value="facility"
                    label="Facility"
                    variant="outline"
                    size="sm"
                    color="orange"
                  />
                </Box>
              )}
              {checkInTypes.has("client") && (
                <Box
                  className={classes.radioBox}
                  style={{
                    cursor: "pointer",
                    border:
                      selectedType === "client" ? "1px solid #FEB273" : "",
                    backgroundColor:
                      selectedType === "client" ? "#feb2732b" : "",
                    width: boxWidth,
                  }}
                  onClick={() => setSelectedType("client")}
                >
                  <Radio
                    value="client"
                    label="Client"
                    variant="outline"
                    size="sm"
                    color="orange"
                  />
                </Box>
              )}
            </Group>
          </Radio.Group>
        ) : (
          <Text ff={"Inter"} fz={14} fw={500} color="red" my={20} ta={"center"}>
            No Facility clock-in type "Facility" or "Client" assigned
          </Text>
        )}

        {/* <Group mt="xs">
            {profile?.user?.facilityId?.find(
              (_f) => _f?.checkInType === "facility"
            ) && <Radio value="facility" label="Facility Type" />}
            {profile?.user?.facilityId?.find(
              (_f) => _f?.checkInType === "client"
            ) && <Radio value="client" label="Client Type" />}
          </Group> */}

        {selectedType === "facility" && (
          <Tooltip
            label={`${
              selectedType === "facility" || selectedType === "no-clockin"
                ? "Facility"
                : "Client"
            } chock-in type is not assigned to facility`}
            position="bottom"
            withArrow
            disabled={facilities?.length > 0}
          >
            <div>
              <Select
                mt={10}
                label="Select Facility"
                size="md"
                data={facilities?.map((_f) => ({
                  label: _f?.name,
                  value: _f?.id,
                }))}
                placeholder="Select Facility"
                value={facilityValue}
                rightSectionPointerEvents="none"
                rightSection={icon}
                onChange={onChangeFacility}

                //disabled={view}
              />
            </div>
          </Tooltip>
        )}

        {selectedType === "client" && (
          <Select
            label="Select Client"
            size="md"
            data={allPatients
              ?.filter(
                (_p) =>
                  _p?.deleted === false &&
                  _p?.active === true &&
                  // Exclude clients whose facilities are all "no-clockin"
                  _p?.facilityId?.some((fRef) => {
                    const fId =
                      typeof fRef === "string" ? fRef : fRef?.id || fRef?._id;
                    return facilities?.some((f) => (f?.id || f?._id) === fId);
                  }),
              )
              ?.map((_p) => ({
                label: _p?.name,
                value: _p?.id,
              }))}
            value={selectedClient}
            placeholder="Select Client"
            onChange={onChangeClient}
            rightSectionPointerEvents="none"
            rightSection={icon}
            mt={10}
            //disabled={view}
          />
        )}

        {selectedType === "client" && (
          <Select
            // className={classes.facilityModal}
            label="Select Service"
            size="md"
            data={allPatients
              ?.filter(
                (_p) =>
                  _p?.deleted === false &&
                  _p?.active === true &&
                  _p?.id === selectedClient,
              )
              ?.flatMap((_p) => {
                let i = _p?.form1?.services?.map((ser) => ({
                  label: ser?.servicetitle,
                  value: ser?.servicenumber,
                }));
                return i || [];
              })}
            value={selectedService}
            placeholder="Select Service"
            onChange={onChangeService}
            rightSectionPointerEvents="none"
            rightSection={icon}
            mt={10}
            disabled={!selectedClient}
          />
        )}

        {selectedType === "client" && selectedClient && (
          <Box mt={8}>
            <Text
              fz={13}
              fw={500}
              color="#FFAF4A"
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                setShowOverrideAddress(!showOverrideAddress);
                if (showOverrideAddress) {
                  setOverrideAddress("");
                  setOverrideSuggestions([]);
                }
              }}
            >
              {showOverrideAddress
                ? "Use client's registered address"
                : "Client is in a different location"}
            </Text>
            {showOverrideAddress && (
              <Box style={{ position: "relative" }} mt={6}>
                <TextInput
                  size="sm"
                  placeholder="Enter service address..."
                  value={overrideAddress}
                  onChange={(e) => handleOverrideSearch(e.target.value)}
                  onBlur={() =>
                    setTimeout(() => setShowOverrideSuggestions(false), 200)
                  }
                  onFocus={() =>
                    overrideSuggestions.length > 0 &&
                    setShowOverrideSuggestions(true)
                  }
                  rightSection={overrideLoading ? <Loader size="xs" /> : null}
                />
                {showOverrideSuggestions && overrideSuggestions.length > 0 && (
                  <Box
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 100,
                      background: "#fff",
                      border: "1px solid #dee2e6",
                      borderRadius: 8,
                      maxHeight: 200,
                      overflowY: "auto",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  >
                    {overrideSuggestions.map((s, i) => (
                      <Box
                        key={s.place_id || i}
                        style={{
                          padding: "10px 14px",
                          cursor: "pointer",
                          fontSize: 14,
                        }}
                        sx={{ "&:hover": { background: "#f8f9fa" } }}
                        onMouseDown={() => handleSelectOverrideAddress(s)}
                      >
                        {s.formatted_address}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <p className={classes.warn}>{warningText}</p>

        {(checkInTypes.has("facility") || checkInTypes.has("client")) && (
          <Box className={classes.distanceSection}>
            <Box
              className={classes.distanceSectionBox}
              style={{ borderBottom: "1px solid #E5E5E5" }}
            >
              <Text fw={700} ff={"Inter"} fz={14}>
                {" "}
                Address
              </Text>
              {isClockLoading ? (
                <Skeleton height={14} mt={6} radius="sm" />
              ) : selectedAddress ? (
                <div style={{ fontWeight: "lighter" }}>{selectedAddress}</div>
              ) : null}
            </Box>
            <Box className={[classes.distanceSectionBox, classes.distance]}>
              <div
                className={classes.iconBox}
                style={{ background: milesError ? "#FEF3F2" : "#D9F2EF" }}
              >
                <img
                  src={`/assets/images/${
                    milesError ? "distance.png" : "distance_green.png"
                  }`}
                  alt="logo"
                  style={{ width: "30px", height: "30px", objectFit: "cover" }}
                />
              </div>
              {isClockLoading ? (
                <Skeleton height={14} width={100} radius="sm" />
              ) : miles ? (
                <div>{miles} Miles away</div>
              ) : null}
            </Box>
          </Box>
        )}

        <Flex justify="flex-end">
          <Group mt="xl">
            <Button onClick={handleClose} className={classes.cancelBtn}>
              Cancel
            </Button>
            <Button
              className={classes.action}
              onClick={clockInHandler}
              loading={isClockLoading}
              disabled={
                selectedType === "client" && !selectedClient
                  ? true
                  : selectedType !== "client" && !selectedFacility
                    ? true
                    : selectedFacility && warningText
                      ? true
                      : milesError
                        ? true
                        : addressNotFound
                          ? true
                          : false
              }
            >
              Clock-in
            </Button>
          </Group>
        </Flex>
      </Modal>
    </>
  );
}

export default SelectCheckInType;
